import { ref } from 'vue'
import { LEAGUE_IDS, type LeagueDivision, type LeagueId, type RankedQueueCall } from '@shared/index'

const SKIP_KEY = 'dc.rankedCall.skip'
const STAGE_KEY = 'dc.rankedCall.accept'
const CALL_ID = /^[A-Za-z0-9_-]{8,32}$/
const SKIP_MAX = 24

/** True only while this tab is in a vs-AI duel. Socket pushes before that are dropped. */
let callsArmed = false

type ClaimFns = {
  send: (id: string) => void
  cancel: (id: string) => void
}

let claimFns: ClaimFns | null = null
let claimPending: { id: string; finish: (ok: boolean) => void } | null = null
/** Claims the player dismissed before the server answered. */
const abortedClaims = new Set<string>()

function loadSkip(): string[] {
  if (typeof sessionStorage === 'undefined') return []
  try {
    const raw = sessionStorage.getItem(SKIP_KEY)
    const parsed: unknown = raw ? JSON.parse(raw) : []
    if (!Array.isArray(parsed)) return []
    return parsed
      .filter((id): id is string => typeof id === 'string' && CALL_ID.test(id))
      .slice(-SKIP_MAX)
  } catch {
    return []
  }
}

/** Waiters the last vs-AI pulse reported. Empty when nobody is in queue. */
export const rankedCallOffers = ref<RankedQueueCall[]>([])

/** Call ids this tab already dismissed. */
export const rankedCallSkip = ref<string[]>(loadSkip())

/**
 * Follow-up taps are ignored until this timestamp. A phone sends a second
 * touch onto whatever sits under the finger after a menu closes.
 */
let activationBlockedUntil = 0
let activationGuardUsers = 0

function outsideRankedCall(target: EventTarget | null): boolean {
  const el =
    target instanceof Element
      ? target
      : target instanceof Node
        ? target.parentElement
        : null
  if (!el) return true
  return !el.closest('[data-ranked-call]')
}

function noteOutsidePress(target: EventTarget | null): void {
  if (!outsideRankedCall(target)) return
  const mark = performance.now() + 450
  activationBlockedUntil = mark
  requestAnimationFrame(() => {
    if (activationBlockedUntil !== mark) return
    if (target instanceof Node && target.isConnected) activationBlockedUntil = 0
  })
}

function onActivationPointerDown(event: PointerEvent): void {
  noteOutsidePress(event.target)
}

function onActivationClick(event: MouseEvent): void {
  noteOutsidePress(event.target)
}

/** One listener while any ranked-call note is on screen. */
export function bindRankedActivationGuard(): () => void {
  if (activationGuardUsers === 0 && typeof window !== 'undefined') {
    window.addEventListener('pointerdown', onActivationPointerDown, true)
    window.addEventListener('click', onActivationClick, true)
  }
  activationGuardUsers += 1
  return () => {
    activationGuardUsers = Math.max(0, activationGuardUsers - 1)
    if (activationGuardUsers > 0 || typeof window === 'undefined') return
    window.removeEventListener('pointerdown', onActivationPointerDown, true)
    window.removeEventListener('click', onActivationClick, true)
  }
}

export function rankedActivationBlocked(): boolean {
  return performance.now() < activationBlockedUntil
}

export function blockRankedActivation(ms = 800): void {
  const until = performance.now() + ms
  if (until > activationBlockedUntil) activationBlockedUntil = until
}

function readLeague(raw: unknown): LeagueId | null {
  return typeof raw === 'string' && (LEAGUE_IDS as readonly string[]).includes(raw)
    ? (raw as LeagueId)
    : null
}

function readDivision(raw: unknown, league: LeagueId | null): LeagueDivision | null {
  if (!league || league === 'king') return null
  const n = typeof raw === 'number' ? raw : Number(raw)
  return n === 1 || n === 2 ? n : null
}

function readPlacement(raw: unknown, league: LeagueId | null): number {
  const n = typeof raw === 'number' ? raw : Number(raw)
  if (Number.isInteger(n) && n >= 0 && n <= 20) return n
  return league ? 0 : 1
}

function readLevel(raw: unknown): number {
  const n = typeof raw === 'number' ? raw : Number(raw)
  if (Number.isInteger(n) && n >= 1 && n <= 9999) return n
  return 1
}

export function readRankedCalls(data: unknown): RankedQueueCall[] {
  if (!data || typeof data !== 'object') return []
  const raw = (data as { calls?: unknown }).calls
  if (!Array.isArray(raw)) return []
  const out: RankedQueueCall[] = []
  for (const row of raw) {
    if (!row || typeof row !== 'object') continue
    const id = (row as { id?: unknown }).id
    const username = (row as { username?: unknown }).username
    if (typeof id !== 'string' || !CALL_ID.test(id)) continue
    if (typeof username !== 'string') continue
    const name = username.replace(/[\u0000-\u001f]/g, '').trim().slice(0, 24)
    if (!name) continue
    const league = readLeague((row as { league?: unknown }).league)
    out.push({
      id,
      username: name,
      league,
      division: readDivision((row as { division?: unknown }).division, league),
      placementRemaining: readPlacement(
        (row as { placementRemaining?: unknown }).placementRemaining,
        league,
      ),
      level: readLevel((row as { level?: unknown }).level),
    })
    if (out.length >= 8) break
  }
  return out
}

export function armRankedCalls(on: boolean): void {
  callsArmed = on
  if (!on) rankedCallOffers.value = []
}

export function dropRankedCall(id: string): void {
  rankedCallOffers.value = rankedCallOffers.value.filter((row) => row.id !== id)
}

export function bindRankedClaim(fns: ClaimFns | null): void {
  claimFns = fns
}

/** Ask the server for this waiter. False means someone else already took them. */
export function requestRankedCall(id: string): Promise<boolean> {
  if (!CALL_ID.test(id) || !claimFns) return Promise.resolve(false)
  abortedClaims.delete(id)
  claimPending?.finish(false)
  return new Promise((resolve) => {
    const timer = window.setTimeout(() => {
      if (claimPending?.id !== id) return
      claimFns?.cancel(id)
      claimPending = null
      resolve(false)
    }, 8_000)
    claimPending = {
      id,
      finish: (ok) => {
        window.clearTimeout(timer)
        resolve(ok)
      },
    }
    claimFns?.send(id)
  })
}

export function settleRankedClaim(id: string, ok: boolean): void {
  if (abortedClaims.has(id)) {
    abortedClaims.delete(id)
    if (ok) claimFns?.cancel(id)
    if (claimPending?.id !== id) return
    const aborted = claimPending
    claimPending = null
    aborted.finish(false)
    return
  }
  if (claimPending?.id !== id) return
  const pending = claimPending
  claimPending = null
  pending.finish(ok)
}

function sameRankedCall(row: RankedQueueCall, next: RankedQueueCall | undefined): boolean {
  return (
    !!next &&
    row.id === next.id &&
    row.username === next.username &&
    row.league === next.league &&
    row.division === next.division &&
    row.placementRemaining === next.placementRemaining &&
    row.level === next.level
  )
}

export function applyRankedCalls(next: RankedQueueCall[]): void {
  if (!callsArmed) return
  const prev = rankedCallOffers.value
  if (
    prev.length === next.length &&
    prev.every((row, index) => sameRankedCall(row, next[index]))
  ) {
    return
  }
  rankedCallOffers.value = next
}

/** Drop a claim that already succeeded so the waiter returns to the queue. */
export function releaseRankedClaim(id: string): void {
  if (!CALL_ID.test(id)) return
  claimFns?.cancel(id)
}

export function ignoreRankedCall(id: string): void {
  if (!CALL_ID.test(id)) return
  blockRankedActivation(800)
  const next = [...rankedCallSkip.value.filter((row) => row !== id), id].slice(-SKIP_MAX)
  rankedCallSkip.value = next
  try {
    sessionStorage.setItem(SKIP_KEY, JSON.stringify(next))
  } catch {
    /* a full storage must not block the duel */
  }
  abortedClaims.add(id)
  if (abortedClaims.size > SKIP_MAX) {
    const oldest = abortedClaims.values().next().value
    if (oldest) abortedClaims.delete(oldest)
  }
  const pending = claimPending?.id === id ? claimPending : null
  if (pending) claimPending = null
  claimFns?.cancel(id)
  pending?.finish(false)
  try {
    if (sessionStorage.getItem(STAGE_KEY) === id) sessionStorage.removeItem(STAGE_KEY)
  } catch {
    /* leaving the staged token would only matter on the next Quick Match */
  }
}

/** Remember a direct ranked pair until the next Quick Match connects. */
export function stageRankedCall(id: string): void {
  if (!CALL_ID.test(id)) return
  try {
    sessionStorage.setItem(STAGE_KEY, id)
  } catch {
    /* the duel button no-ops if this cannot be stored */
  }
}

/** Read and clear the staged pair token. Empty when there is none. */
export function takeStagedRankedCall(): string {
  try {
    const raw = sessionStorage.getItem(STAGE_KEY) ?? ''
    sessionStorage.removeItem(STAGE_KEY)
    return CALL_ID.test(raw) ? raw : ''
  } catch {
    return ''
  }
}
